import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowUUpLeft,
  Camera,
  PencilSimple,
  Scissors,
  Trash,
  Printer,
  FilePdf,
  LinkSimple,
  UploadSimple,
  SquaresFour,
  Images,
  Tag,
  Stack,
  Note,
  LinkSimpleHorizontal,
  DotsThreeVertical,
  CaretDown,
  Eye,
  EyeSlash,
} from "@phosphor-icons/react";
import { RemnantFromSlabDialog } from "@/components/RemnantFromSlabDialog";
import { ItemLinks } from "@/components/ItemLinks";
import { SegTabs, useSwipeTabs } from "@/components/SegTabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SlabPieces } from "@/components/SlabPieces";
import { ColorChips } from "@/components/ColorChips";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AppHeader } from "@/components/AppHeader";
import { SlabLabel } from "@/components/SlabLabel";
import { PhotoGallery } from "@/components/PhotoGallery";
import { PhotoEditor } from "@/components/PhotoEditor";
import { hasPendingHd, usePendingHdRefresh } from "@/lib/photoHd";
import { CUSTOMER_GROUPS } from "@/pages/Customers";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shrinkForUpload } from "@/lib/shrinkImage";
import { useDirectory } from "@/lib/directory";

const STATUSES = ["In Stock", "Out of Stock", "Reserved", "Consumed"];
const STATUS_BADGE = {
  "In Stock": "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
  "Out of Stock": "border-red-500/25 bg-red-500/10 text-red-400",
  Reserved: "border-amber-500/25 bg-amber-500/10 text-amber-400",
  Consumed: "border-zinc-500/25 bg-zinc-500/10 text-zinc-400",
};

const TABS = [
  { key: "details", label: "Details", icon: SquaresFour },
  { key: "pricing", label: "Pricing", icon: Tag },
  { key: "photos", label: "Photos", icon: Images },
  { key: "notes", label: "Notes", icon: Note },
  { key: "pieces", label: "Slabs", icon: Stack },
  { key: "usage", label: "Usage", icon: LinkSimpleHorizontal },
];

export default function SlabDetail() {
  const { id } = useParams();
  const { nameOf } = useDirectory();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState(null);
  const [remnantOpen, setRemnantOpen] = useState(false);

  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [tab, setTab] = useState("details");
  const swipe = useSwipeTabs(TABS, tab, (t) => setTab(t.key));
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceDraft, setPriceDraft] = useState({
    cost: "",
    sale: "",
    groups: {},
  });
  const [savingPrice, setSavingPrice] = useState(false);
  const [groupSetup, setGroupSetup] = useState({
    discounts: {},
    visible_groups: [],
    portal_prices_on: false,
  });
  const [urlOpen, setUrlOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [qtyDraft, setQtyDraft] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [links, setLinks] = useState(null);
  const [allocOpen, setAllocOpen] = useState(false);

  const saveQuantity = async () => {
    try {
      const { data } = await api.patch(`/slabs/${id}/quantity`, {
        quantity: Number(qtyDraft),
      });
      setS(data);
      setQtyDraft(null);
      toast.success(`Quantity set to ${data.quantity}`);
    } catch {
      toast.error("Could not update quantity");
    }
  };

  const addPhotoFromUrl = async () => {
    setUploading(true);
    try {
      const { data } = await api.post(`/slabs/${id}/photos/from-url`, {
        url: photoUrl.trim(),
      });
      setS(data);
      setPhotoUrl("");
      setUrlOpen(false);
      toast.success("Photo added from link");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not fetch that image",
      );
    }
    setUploading(false);
  };
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const labelRef = useRef(null);

  const setStatus = async (status) => {
    if (status === s.status) return;
    try {
      const { data } = await api.patch(`/slabs/${id}/status`, { status });
      setS(data);
      toast.success(`${data.code} → ${status}`);
    } catch {
      toast.error("Could not change status");
    }
  };

  const togglePublicVisible = async () => {
    const next = s.public_visible === false;
    try {
      const { data } = await api.patch(`/slabs/${id}/flags`, { public_visible: next });
      setS(data);
      toast.success(
        next
          ? "Showing on the public site & customer portal"
          : "Hidden from the public site & customer portal",
      );
    } catch {
      toast.error("Could not change visibility");
    }
  };

  const savePricing = async () => {
    setSavingPrice(true);
    try {
      const { data } = await api.patch(`/slabs/${id}/pricing`, {
        cost_per_sqft: priceDraft.cost === "" ? null : Number(priceDraft.cost),
        sale_price_per_sqft:
          priceDraft.sale === "" ? null : Number(priceDraft.sale),
        group_prices: Object.fromEntries(
          Object.entries(priceDraft.groups || {})
            .filter(([, v]) => v !== "" && v !== null)
            .map(([g, v]) => [g, Number(v)]),
        ),
      });
      setS(data);
      setEditingPrice(false);
      toast.success("Pricing saved");
    } catch {
      toast.error("Could not save pricing");
    }
    setSavingPrice(false);
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const { data } = await api.patch(`/slabs/${id}/notes`, {
        notes: notesDraft,
      });
      setS(data);
      setEditingNotes(false);
      toast.success("Notes saved");
    } catch {
      toast.error("Could not save notes");
    }
    setSavingNotes(false);
  };

  useEffect(() => {
    api
      .get(`/slabs/${id}`)
      .then(({ data }) => setS(data))
      .catch(() => toast.error("Slab not found"));
    api
      .get("/settings/group-pricing")
      .then(({ data }) => setGroupSetup(data))
      .catch(() => {});
    api
      .get(`/inventory/slabs/${id}/links`)
      .then(({ data }) => setLinks(data))
      .catch(() => {});
  }, [id]);

  const refreshSlab = useCallback(
    () =>
      api
        .get(`/slabs/${id}`)
        .then(({ data }) => setS(data))
        .catch(() => {}),
    [id],
  );
  usePendingHdRefresh(
    hasPendingHd(s?.photos) ||
      (s?.pieces || []).some((pc) => hasPendingHd(pc.photos)),
    refreshSlab,
  );

  // photoId set means we are swapping an existing photo, not adding a new one
  const savePhoto = async (file, photoId) => {
    setUploading(true);
    file = await shrinkForUpload(file);
    const fd = new FormData();
    fd.append("file", file);
    if (file.enhanced) fd.append("enhanced", "true");
    try {
      const { data } = await api.post(
        photoId ? `/slabs/${id}/photos/${photoId}` : `/slabs/${id}/photos`,
        fd,
      );
      setS(data);
      toast.success(
        photoId
          ? "Photo updated"
          : hasPendingHd(data.photos)
            ? "Photo attached — HD version on its way"
            : "Photo attached",
      );
    } catch (err) {
      toast.error("Photo upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const revertHd = async (photo) => {
    try {
      const { data } = await api.post("/photos/revert-hd", {
        collection: "slabs",
        doc_id: id,
        photo_id: photo.id,
      });
      setS(data);
      toast.success("Original photo restored");
    } catch {
      toast.error("Could not restore the original");
    }
  };

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (file) setPending({ file, photoId: null });
    e.target.value = "";
  };

  const editPhoto = async (p) => {
    try {
      const { data } = await api.get(`/photos/${p.storage_path}`, {
        responseType: "blob",
      });
      setPending({
        photoId: p.id,
        file: new File([data], p.original_filename || "photo.jpg", {
          type: data.type || "image/jpeg",
        }),
      });
    } catch {
      toast.error("Could not open that photo");
    }
  };

  const removePhoto = async (photoId) => {
    const { data } = await api.delete(`/slabs/${id}/photos/${photoId}`);
    setS(data);
    toast.success("Photo removed");
  };

  const cutoutPhoto = async (photo) => {
    try {
      const { data } = await api.post(photo.cutout ? "/photos/cutout/undo" : "/photos/cutout", {
        collection: "slabs",
        doc_id: id,
        photo_id: photo.id,
      });
      setS(data);
      toast.success(photo.cutout ? "Original restored" : "Background removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not edit the photo");
    }
  };

  const reorderPhotos = async (orderIds) => {
    try {
      const { data } = await api.put(`/slabs/${id}/photos/order`, {
        order: orderIds,
      });
      setS(data);
    } catch {
      toast.error("Could not save the new photo order");
    }
  };

  const toggleWebsite = async (photo) => {
    try {
      const { data } = await api.put(`/slabs/${id}/photos/${photo.id}/website`, {
        show: photo.show_on_website === false,
      });
      setS(data);
      toast.success(
        photo.show_on_website === false
          ? "Photo will show on the website"
          : "Photo hidden from the website",
      );
    } catch {
      toast.error("Could not update website visibility");
    }
  };
  const toggleEdge = async (photo) => {
    try {
      const { data } = await api.put(`/slabs/${id}/photos/${photo.id}/edge`, {
        on: !photo.edge_texture,
      });
      setS(data);
      toast.success(
        photo.edge_texture
          ? "Edge previews go back to the first website photo"
          : "This photo now drives the website's 3D edge previews",
      );
    } catch {
      toast.error("Could not update the edge photo");
    }
  };

  const downloadPdf = async () => {
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import("jspdf"),
      import("html2canvas"),
    ]);
    const canvas = await html2canvas(labelRef.current, {
      scale: 3,
      backgroundColor: "#ffffff",
    });
    const pdf = new jsPDF({
      unit: "in",
      format: [4, 6],
      orientation: "portrait",
    });
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, 4, 6);
    pdf.save(`${s.code}-label.pdf`);
    toast.success("Label PDF downloaded");
  };

  const remove = async () => {
    await api.delete(`/slabs/${id}`);
    toast.success("Slab record deleted");
    navigate("/inventory/slabs");
  };

  if (!s)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Slab" />
        <div className="mx-auto max-w-3xl px-4 pt-10">
          <div className="h-40 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900" />
        </div>
      </div>
    );

  const photos = (s.photos || []).filter((p) => !p.is_deleted);
  const sqftEach = Math.round(((s.length_in * s.width_in) / 144) * 10) / 10;
  const totalSqft =
    Math.round(
      ((s.pieces || []).length
        ? (s.pieces || []).reduce(
            (sum, p) =>
              sum +
              ((Number(p.length_in) || s.length_in || 0) *
                (Number(p.width_in) || s.width_in || 0)) /
                144,
            0,
          )
        : sqftEach * (s.quantity || 1)) * 10,
    ) / 10;
  const rows = [
    ["Material", `${s.material} · ${s.finish}`],
    ...(s.primary_color || s.accent_color
      ? [
          [
            "Colors",
            <ColorChips
              key="colors"
              primary={s.primary_color}
              accent={s.accent_color}
              testId="slab-detail-colors-chips"
            />,
          ],
        ]
      : []),
    ["Thickness", s.thickness],
    ["Size", `${s.length_in}" x ${s.width_in}"`],
    ["Quantity", `${s.quantity}`],
    ["Sq Ft Each", `${sqftEach} ft²`],
    ["Total Sq Ft", `${totalSqft} ft²`],
    ["Stone Group", s.stone_group || "—"],
    ["Brand", s.brand || "—"],
    ["Supplier", s.supplier || "—"],
    ["Rack", s.location || "—"],
    [
      "Stock type",
      s.stocked === false
        ? "Supplier stock (not counted)"
        : "Tracked inventory",
    ],
    ["Consignment", s.consignment ? "Yes — supplier owned" : "No"],
    ["Logged By", nameOf(s.created_by) || "—"],
  ].filter(Boolean);

  const moneyRows = [
    ["Cost / Sq Ft", s.cost_per_sqft != null ? `$${s.cost_per_sqft}` : "—"],
    [
      "Sale / Sq Ft",
      s.sale_price_per_sqft != null ? `$${s.sale_price_per_sqft}` : "—",
    ],
    [
      "Total Cost",
      s.cost_per_sqft != null
        ? `$${Math.round(s.cost_per_sqft * totalSqft * 100) / 100}`
        : "—",
    ],
    [
      "Total Sale",
      s.sale_price_per_sqft != null
        ? `$${Math.round(s.sale_price_per_sqft * totalSqft * 100) / 100}`
        : "—",
    ],
  ];

  const groupRow = (g) => {
    const own = (s.group_prices || {})[g];
    if (own !== undefined && own !== null && own !== "")
      return { price: Number(own), source: "set", off: 0 };
    if (s.sale_price_per_sqft != null) {
      const off = Number(groupSetup.discounts?.[g] || 0);
      return {
        price: Math.round(s.sale_price_per_sqft * (1 - off / 100) * 100) / 100,
        source: "discount",
        off,
      };
    }
    const factor = Number(groupSetup.natural_multipliers?.[g] || 0);
    const natural = (groupSetup.natural_materials || []).includes(
      (s.material || "").trim().toLowerCase(),
    );
    if (factor && natural && s.cost_per_sqft != null)
      return {
        price: Math.round(s.cost_per_sqft * factor * 100) / 100,
        source: "multiplier",
        factor,
        off: 0,
      };
    return { price: null, source: "none", off: 0 };
  };

  const slabPieces = s.pieces || [];
  const pieceSqft = (p) =>
    Math.round(
      (((Number(p.length_in) || s.length_in || 0) *
        (Number(p.width_in) || s.width_in || 0)) /
        144) *
        10,
    ) / 10;
  const sumSqft = (arr) =>
    Math.round(arr.reduce((n, p) => n + pieceSqft(p), 0) * 10) / 10;
  const nonConsumed = slabPieces.filter(
    (p) => (p.status || "In Stock") !== "Consumed",
  );
  const reservedPieces = slabPieces.filter((p) => p.status === "Reserved");
  const inStockPieces = slabPieces.filter(
    (p) => (p.status || "In Stock") === "In Stock",
  );
  const stOnHand = slabPieces.length
    ? nonConsumed.length
    : s.status === "Consumed"
      ? 0
      : s.quantity || 0;
  const stAlloc = slabPieces.length
    ? reservedPieces.length
    : s.status === "Reserved"
      ? s.quantity || 0
      : 0;
  const stAvail = slabPieces.length
    ? inStockPieces.length
    : s.status === "In Stock"
      ? s.quantity || 0
      : 0;
  const stOnHandSqft = slabPieces.length
    ? sumSqft(nonConsumed)
    : Math.round(sqftEach * stOnHand * 10) / 10;
  const stAvailSqft = slabPieces.length
    ? sumSqft(inStockPieces)
    : Math.round(sqftEach * stAvail * 10) / 10;
  const stPo = (links?.purchase_orders || [])
    .filter((p) => ["Draft", "Ordered", "Partial"].includes(p.status))
    .reduce(
      (n, p) => n + Math.max(0, (Number(p.qty) || 0) - (Number(p.received_qty) || 0)),
      0,
    );
  const statCards = [
    { key: "in-stock", label: "In Stock", units: stOnHand, sqft: stOnHandSqft, tone: "text-emerald-300" },
    { key: "allocated", label: "Allocated", units: stAlloc, sqft: null, tone: "text-amber-300" },
    { key: "available", label: "Available", units: stAvail, sqft: stAvailSqft, tone: "text-sky-300" },
    { key: "on-po", label: "On P.O.", units: stPo, sqft: null, tone: "text-indigo-300" },
  ];

  return (
    <div className="grain min-h-screen">
      <AppHeader title={s.code} />
      <main className="relative z-10 mx-auto max-w-5xl px-4 pb-24 pt-6">
        <button
          onClick={() => navigate("/inventory/slabs")}
          data-testid="back-button"
          className="no-print mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-150 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> All slabs
        </button>

        <div
          className="no-print rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="slab-header-card"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-mono-data text-xs uppercase tracking-widest text-zinc-500">
                {s.code}
              </div>
              <h1
                className="text-2xl font-bold tracking-tight sm:text-3xl"
                data-testid="slab-detail-color"
              >
                {s.slab_color}
                <span
                  data-testid="slab-status-badge"
                  className={`ml-3 inline-block rounded-full border px-2.5 py-0.5 align-middle text-[10px] font-semibold uppercase leading-tight tracking-widest ${
                    STATUS_BADGE[s.status] || STATUS_BADGE.Consumed
                  }`}
                >
                  {s.status}
                </span>
                {s.stocked === false && (
                  <span
                    data-testid="slab-nonstock-badge"
                    className="ml-2 inline-block rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-0.5 align-middle text-[10px] font-semibold uppercase leading-tight tracking-widest text-amber-400"
                  >
                    Supplier stock
                  </span>
                )}
              </h1>
              <p className="mt-1 text-sm text-zinc-400">
                {s.material} · {s.finish} · {s.thickness}
              </p>
              <div
                className="mt-3 flex flex-wrap gap-2"
                data-testid="slab-status-quick-edit"
              >
                {STATUSES.map((v) => (
                  <button
                    key={v}
                    data-testid={`slab-set-status-${v.toLowerCase().replace(/ /g, "-")}`}
                    onClick={() => setStatus(v)}
                    className={`rounded-full border px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors duration-150 ${
                      s.status === v
                        ? "border-indigo-400/50 bg-indigo-500/10 text-indigo-300"
                        : "border-zinc-800 bg-zinc-900 text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {v}
                  </button>
                ))}
                <span className="mx-1 h-6 w-px self-center bg-zinc-800" />
                {qtyDraft === null ? (
                  <button
                    data-testid="slab-edit-quantity-button"
                    onClick={() => setQtyDraft(s.quantity ?? 1)}
                    className="rounded-full border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-zinc-200"
                  >
                    Qty {s.quantity ?? 1}
                  </button>
                ) : (
                  <span className="flex items-center gap-2">
                    <Input
                      data-testid="slab-quantity-input"
                      type="number"
                      min="0"
                      autoFocus
                      value={qtyDraft}
                      onChange={(e) => setQtyDraft(e.target.value)}
                      className="h-9 w-20 border-zinc-800 bg-zinc-950"
                    />
                    <Button
                      data-testid="slab-save-quantity-button"
                      onClick={saveQuantity}
                      className="h-9 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      Save
                    </Button>
                    <button
                      data-testid="slab-cancel-quantity-button"
                      onClick={() => setQtyDraft(null)}
                      className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
                    >
                      Cancel
                    </button>
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="slab-actions-menu"
                    variant="outline"
                    className="h-9 border-zinc-700 bg-zinc-800/60 px-3 text-xs font-semibold uppercase tracking-widest text-zinc-200 hover:bg-zinc-800"
                  >
                    Actions
                    <DotsThreeVertical size={16} weight="bold" className="ml-1.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 border-zinc-800 bg-zinc-900">
                  <DropdownMenuItem data-testid="menu-edit-slab" onClick={() => navigate(`/inventory/slabs/${id}/edit`)}>
                    <PencilSimple size={15} weight="bold" className="mr-2" /> Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-print-label" onClick={() => window.print()}>
                    <Printer size={15} weight="bold" className="mr-2" /> Print 4x6 label
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-slab-pdf" onClick={downloadPdf}>
                    <FilePdf size={15} weight="bold" className="mr-2" /> Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem data-testid="menu-create-remnant" onClick={() => setRemnantOpen(true)}>
                    <Scissors size={15} weight="bold" className="mr-2" /> Create remnant
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-start-return" onClick={() => navigate(`/inventory/rmas/new?slab=${id}&print=1`)}>
                    <ArrowUUpLeft size={15} weight="bold" className="mr-2" /> Start supplier return
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem
                    data-testid="menu-delete-slab"
                    onClick={() => setDelOpen(true)}
                    className="text-red-400 focus:text-red-300"
                  >
                    <Trash size={15} weight="bold" className="mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <div
                className="font-mono-data text-3xl font-bold text-yellow-400"
                data-testid="slab-detail-total-sqft"
              >
                {totalSqft} ft²
              </div>
            </div>
          </div>
        </div>

        {remnantOpen && (
          <RemnantFromSlabDialog
            slab={s}
            open={remnantOpen}
            onClose={() => setRemnantOpen(false)}
            onSlab={setS}
          />
        )}
        <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
          <AlertDialogContent className="border-zinc-800 bg-zinc-900">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {s.code}?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                This removes the slab record from stock.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-slab-delete" className="border-zinc-700">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-slab-delete"
                onClick={remove}
                className="bg-red-600 text-white hover:bg-red-500"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div
          className="no-print mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4"
          data-testid="slab-stat-cards"
        >
          {statCards.map((c) => (
            <div
              key={c.key}
              data-testid={`slab-stat-${c.key}`}
              onClick={c.key === "allocated" ? () => setAllocOpen((v) => !v) : undefined}
              className={`rounded-lg border border-zinc-800 bg-zinc-900 p-4 ${
                c.key === "allocated"
                  ? "cursor-pointer transition-colors hover:border-amber-500/40"
                  : ""
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {c.label}
                </span>
                {c.key === "allocated" ? (
                  <CaretDown
                    size={13}
                    weight="bold"
                    className={`text-zinc-500 transition-transform ${allocOpen ? "rotate-180" : ""}`}
                  />
                ) : (
                  c.sqft != null && (
                    <span className={`font-mono-data text-sm font-bold ${c.tone}`}>
                      {c.sqft} ft²
                    </span>
                  )
                )}
              </div>
              <div className="mt-2 font-mono-data text-2xl font-bold text-zinc-100">
                {c.units}
              </div>
              <div className="text-[11px] text-zinc-500">
                unit{c.units === 1 ? "" : "s"}
              </div>
            </div>
          ))}
        </div>

        {allocOpen && (
          <div
            className="no-print mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4"
            data-testid="slab-allocated-breakdown"
          >
            <div className="text-[10px] uppercase tracking-widest text-amber-300/80">
              Allocated to
            </div>
            {stAlloc === 0 ? (
              <p className="mt-2 text-sm text-zinc-500">
                Nothing reserved — all stock is available.
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {reservedPieces.map((p, i) => (
                  <div
                    key={p.piece_id || i}
                    data-testid={`slab-alloc-piece-${i}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm"
                  >
                    <span className="font-mono-data text-zinc-300">Piece {i + 1}</span>
                    <span className="truncate text-zinc-400">
                      {p.reserved_job ? `Job ${p.reserved_job}` : ""}
                      {p.reserved_for
                        ? `${p.reserved_job ? " · " : ""}${p.reserved_for}`
                        : p.reserved_job
                          ? ""
                          : p.held_project_code || p.reservation_tag || "Held"}
                    </span>
                  </div>
                ))}
                {(links?.projects || [])
                  .filter((pr) => (pr.pieces || 0) > 0)
                  .map((pr) => (
                    <button
                      key={pr.id}
                      type="button"
                      onClick={() => navigate(`/projects/${pr.id}`)}
                      data-testid={`slab-alloc-project-${pr.id}`}
                      className="flex w-full items-center justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-left text-sm transition-colors hover:border-amber-400/60"
                    >
                      <span className="truncate">
                        <span className="font-mono-data text-amber-300">{pr.code}</span>{" "}
                        · {pr.name}
                      </span>
                      <span className="shrink-0 text-xs text-zinc-400">
                        {pr.pieces} pc
                      </span>
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}

        <div className="no-print mt-6">
          <SegTabs
            sticky
            testId="slab-tabs"
            tabs={TABS.map((t) =>
              t.key === "photos"
                ? { ...t, badge: photos.length, tone: "info" }
                : t,
            )}
            value={tab}
            onChange={(t) => setTab(t.key)}
          />
        </div>

        <div
          className="mt-6 space-y-6"
          data-testid={`slab-tab-panel-${tab}`}
          {...swipe}
        >
          {tab === "details" && (
            <div className="divide-y divide-zinc-800 rounded-lg border border-zinc-800 bg-zinc-900">
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Specifications
                </div>
              </div>
              {rows.map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-center justify-between gap-4 px-4 py-3"
                >
                  <span className="text-xs uppercase tracking-widest text-zinc-500">
                    {k}
                  </span>
                  <span
                    data-testid={`slab-detail-${k.toLowerCase().replace(/[ /]+/g, "-")}`}
                    className="font-mono-data text-right text-sm font-medium"
                  >
                    {v}
                  </span>
                </div>
              ))}
            </div>
          )}

          {tab === "usage" && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <ItemLinks kind="slabs" itemId={id} item={s} testId="slab-links" />
            </div>
          )}

          {tab === "photos" && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div
                className="flex flex-wrap items-center gap-2"
                data-testid="slab-photos-toolbar"
              >
                <div className="w-full text-xs uppercase tracking-widest text-zinc-500 sm:mr-auto sm:w-auto">
                  Photos
                </div>
                <Button
                  data-testid="slab-take-photo-button"
                  variant="outline"
                  onClick={() => cameraRef.current?.click()}
                  disabled={uploading}
                  title="Open the camera and snap a photo"
                  className="h-10 flex-1 border-zinc-700 bg-transparent px-3 text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800 sm:flex-none"
                >
                  <Camera size={16} weight="bold" className="mr-2" />
                  {uploading ? "Uploading…" : "Take photo"}
                </Button>
                <Button
                  data-testid="slab-add-photo-button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  title="Pick a photo from your library or files"
                  className="h-10 flex-1 border-zinc-700 bg-transparent px-3 text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800 sm:flex-none"
                >
                  <UploadSimple size={16} weight="bold" className="mr-2" />
                  Upload
                </Button>
                <Button
                  data-testid="slab-photo-url-button"
                  variant="outline"
                  onClick={() => setUrlOpen(!urlOpen)}
                  className="h-10 flex-1 border-zinc-700 bg-transparent px-3 text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800 sm:flex-none"
                >
                  <LinkSimple size={16} weight="bold" className="mr-2" /> From
                  URL
                </Button>
                <input
                  ref={cameraRef}
                  data-testid="slab-photo-camera-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={pickPhoto}
                  className="hidden"
                />
                <input
                  ref={fileRef}
                  data-testid="slab-photo-file-input"
                  type="file"
                  accept="image/*"
                  onChange={pickPhoto}
                  className="hidden"
                />
              </div>
              {urlOpen && (
                <div
                  className="my-3 flex flex-wrap gap-2"
                  data-testid="slab-photo-url-panel"
                >
                  <Input
                    data-testid="slab-photo-url-input"
                    autoFocus
                    value={photoUrl}
                    onChange={(e) => setPhotoUrl(e.target.value)}
                    placeholder="https://…/slab-photo.jpg"
                    className="h-11 min-w-[240px] flex-1 border-zinc-800 bg-zinc-950"
                  />
                  <Button
                    data-testid="slab-photo-url-save"
                    disabled={uploading || !photoUrl.trim()}
                    onClick={addPhotoFromUrl}
                    className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                  >
                    {uploading ? "Fetching…" : "Add"}
                  </Button>
                </div>
              )}
              <PhotoGallery
                photos={photos}
                onRemove={removePhoto}
                onEdit={editPhoto}
                onReorder={reorderPhotos}
                onToggleWebsite={toggleWebsite}
                onToggleEdge={toggleEdge}
                onRevertHd={revertHd}
                onCutout={cutoutPhoto}
                testPrefix="slab-photo"
              />
              {pending && (
                <PhotoEditor
                  file={pending.file}
                  onDone={(f) => {
                    const target = pending.photoId;
                    setPending(null);
                    savePhoto(f, target);
                  }}
                  onClose={() => setPending(null)}
                />
              )}
            </div>
          )}

          {tab === "pricing" && (
            <div
              className="no-print rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="slab-pricing-panel"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Cost & Sale
                </div>
                {!editingPrice && (
                  <button
                    data-testid="slab-edit-pricing-button"
                    onClick={() => {
                      setPriceDraft({
                        cost: s.cost_per_sqft ?? "",
                        sale: s.sale_price_per_sqft ?? "",
                        groups: Object.fromEntries(
                          CUSTOMER_GROUPS.map((g) => [
                            g,
                            (s.group_prices || {})[g] ?? "",
                          ]),
                        ),
                      });
                      setEditingPrice(true);
                    }}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-indigo-300 hover:underline"
                  >
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                )}
              </div>
              {editingPrice ? (
                <div className="mt-3 space-y-3">
                  {[
                    ["cost", "Cost / Sq Ft"],
                    ["sale", "Sale / Sq Ft"],
                  ].map(([k, label]) => (
                    <div key={k} className="space-y-1">
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                        {label}
                      </span>
                      <Input
                        data-testid={`slab-${k}-input`}
                        type="number"
                        step="0.01"
                        min="0"
                        value={priceDraft[k]}
                        onChange={(e) =>
                          setPriceDraft({ ...priceDraft, [k]: e.target.value })
                        }
                        placeholder="—"
                        className="h-11 border-zinc-800 bg-zinc-950"
                      />
                    </div>
                  ))}
                  <div className="space-y-2 rounded-md border border-zinc-800 bg-zinc-950 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Customer group price / sq ft
                    </div>
                    <p className="text-[11px] leading-relaxed text-zinc-600">
                      Leave blank to use the sale price less that group's
                      standard discount.
                    </p>
                    {CUSTOMER_GROUPS.map((g) => (
                      <div key={g} className="flex items-center gap-3">
                        <span className="w-24 shrink-0 text-[11px] text-zinc-400">
                          {g}
                        </span>
                        <Input
                          data-testid={`slab-group-price-${g}`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={(priceDraft.groups || {})[g] ?? ""}
                          onChange={(e) =>
                            setPriceDraft((d) => ({
                              ...d,
                              groups: {
                                ...(d.groups || {}),
                                [g]: e.target.value,
                              },
                            }))
                          }
                          placeholder={
                            groupRow(g).price != null
                              ? `auto $${groupRow(g).price}`
                              : "—"
                          }
                          className="h-10 border-zinc-800 bg-zinc-900"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid="slab-save-pricing-button"
                      disabled={savingPrice}
                      onClick={savePricing}
                      className="h-10 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      {savingPrice ? "Saving…" : "Save"}
                    </Button>
                    <Button
                      data-testid="slab-cancel-pricing-button"
                      variant="outline"
                      onClick={() => setEditingPrice(false)}
                      className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 divide-y divide-zinc-800 rounded-md border border-zinc-800 bg-zinc-950">
                  {moneyRows.map(([k, v]) => (
                    <div
                      key={k}
                      className="flex items-center justify-between gap-4 px-3 py-2.5"
                    >
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {k}
                      </span>
                      <span
                        data-testid={`slab-detail-${k.toLowerCase().replace(/[ /]+/g, "-")}`}
                        className="font-mono-data text-right text-sm font-medium"
                      >
                        {v}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div
                className="mt-3 flex items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5"
                data-testid="slab-public-visibility"
              >
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-zinc-200">
                    Public site &amp; portal
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    {s.public_visible === false
                      ? "Hidden from customers"
                      : "Visible to customers"}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="slab-public-visibility-toggle"
                  onClick={togglePublicVisible}
                  className={`flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    s.public_visible === false
                      ? "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
                      : "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                  }`}
                >
                  {s.public_visible === false ? (
                    <>
                      <EyeSlash size={13} weight="bold" /> Hidden
                    </>
                  ) : (
                    <>
                      <Eye size={13} weight="bold" /> Shown
                    </>
                  )}
                </button>
              </div>

              {!editingPrice && (
                <div
                  className="mt-3 rounded-md border border-zinc-800 bg-zinc-950"
                  data-testid="slab-group-pricing"
                >
                  <div className="flex items-center justify-between gap-3 px-3 pb-1 pt-2.5">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Portal price preview
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-600">
                      what each group sees / sq ft
                    </span>
                  </div>
                  <div className="divide-y divide-zinc-800">
                    {CUSTOMER_GROUPS.map((g) => {
                      const row = groupRow(g);
                      const hidden =
                        s.show_price_on_portal === false ||
                        !groupSetup.portal_prices_on ||
                        !(groupSetup.visible_groups || []).includes(g);
                      return (
                        <div
                          key={g}
                          className="flex items-center justify-between gap-3 px-3 py-2"
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="truncate text-xs text-zinc-300">
                              {g}
                            </span>
                            {row.source === "set" ? (
                              <span className="rounded border border-indigo-500/30 bg-indigo-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-indigo-300">
                                Set
                              </span>
                            ) : row.source === "multiplier" ? (
                              <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-amber-300">
                                cost × {row.factor}
                              </span>
                            ) : row.off ? (
                              <span className="rounded border border-zinc-700 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-zinc-500">
                                {row.off}% off
                              </span>
                            ) : null}
                          </span>
                          <span
                            data-testid={`slab-group-price-value-${g}`}
                            className={`shrink-0 text-sm font-medium ${
                              row.price == null
                                ? "font-mono-data text-zinc-600"
                                : hidden
                                  ? "text-[10px] uppercase tracking-widest text-zinc-600"
                                  : "font-mono-data text-emerald-300"
                            }`}
                          >
                            {row.price == null
                              ? "—"
                              : hidden
                                ? "Hidden"
                                : `$${row.price}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {s.show_price_on_portal === false ? (
                    <p className="px-3 pb-2.5 pt-1 text-[10px] leading-relaxed text-amber-400/80">
                      This slab is set to hide its price on the portal, so
                      customers see no price regardless of group.
                    </p>
                  ) : !groupSetup.portal_prices_on ? (
                    <p className="px-3 pb-2.5 pt-1 text-[10px] leading-relaxed text-zinc-600">
                      Portal prices are switched off, so customers see none of
                      these yet.
                    </p>
                  ) : (groupSetup.visible_groups || []).length === 0 ? (
                    <p className="px-3 pb-2.5 pt-1 text-[10px] leading-relaxed text-amber-400/80">
                      No account group is set to see prices, so customers see
                      none of these yet. Turn a group on in Settings → Portal,
                      or set an account's Price Mode to “Always on”.
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {tab === "notes" && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Notes
                </div>
                {!editingNotes && (
                  <button
                    data-testid="slab-edit-notes-button"
                    onClick={() => {
                      setNotesDraft(s.notes || "");
                      setEditingNotes(true);
                    }}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-indigo-300 hover:underline"
                  >
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                )}
              </div>
              {editingNotes ? (
                <div className="mt-3">
                  <Textarea
                    data-testid="slab-notes-input"
                    autoFocus
                    rows={4}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    className="border-zinc-800 bg-zinc-950"
                  />
                  <div className="mt-3 flex gap-2">
                    <Button
                      data-testid="slab-save-notes-button"
                      disabled={savingNotes}
                      onClick={saveNotes}
                      className="h-10 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      {savingNotes ? "Saving…" : "Save"}
                    </Button>
                    <Button
                      data-testid="slab-cancel-notes-button"
                      variant="outline"
                      onClick={() => setEditingNotes(false)}
                      className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p
                  className="mt-2 whitespace-pre-wrap text-sm text-zinc-200"
                  data-testid="slab-detail-notes"
                >
                  {s.notes || "—"}
                </p>
              )}
            </div>
          )}

          {tab === "pieces" && <SlabPieces slab={s} onChange={setS} />}
        </div>

        <div className="hidden print:block">
          <div id="print-area" style={{ width: "4in" }}>
            <SlabLabel s={s} forwardRef={labelRef} />
          </div>
        </div>
      </main>
    </div>
  );
}
